import React, {
	createContext,
	useContext,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { SUPPORT_BUBBLE_GUTTER_PX } from "../support/support-panel-layout";

/**
 * How much screen a view actually has — the one place that answers it.
 *
 * The app's chrome is not a fixed height. Five banners (new version,
 * impersonation, queue health, email sync, sending suspension) come and go on
 * their own polls, and the navbar's toolbar wraps to two rows on a narrow
 * window. Anything that sizes itself against the viewport has to know the
 * chrome's height *now*, which is why every hand-tuned `calc(100vh - 300px)`
 * was wrong the moment a banner appeared.
 *
 * Two ways to consume it:
 *  - `AVAILABLE_HEIGHT` in an `sx` height, for a view sitting directly under
 *    the chrome. No hook, no ref. It leaves out the support bubble's corner
 *    as well as the chrome: the bubble is fixed over the bottom-right, so a
 *    view that filled to the window's bottom put its pagination arrows under
 *    it, and they could not be clicked at all.
 *  - `useAvailableHeight()` for a pane further down the page: it measures the
 *    element's own top, so whatever page header or tab bar sits above it is
 *    accounted for without anyone naming a number.
 */

interface AppViewportContextValue {
	/** Measured height of the navbar and banner region, in px. */
	chromeHeight: number;
}

const AppViewportContext = createContext<AppViewportContextValue>({ chromeHeight: 0 });

/** Viewport height minus the app chrome. Use directly in an `sx` height. */
export const AVAILABLE_HEIGHT = "var(--odi-available-height, 100vh)";

/** Height a measured pane never drops below, so a tall stack of banners
 * leaves something readable rather than a sliver. */
const MIN_PANE_PX = 240;

const CHROME_HEIGHT_VAR = "--odi-chrome-height";
const AVAILABLE_HEIGHT_VAR = "--odi-available-height";

interface AppViewportProviderProps {
	/** The navbar and banner region — everything above the routed view. */
	chrome: React.ReactNode;
	children: React.ReactNode;
}

/**
 * Measures the app chrome and publishes it, both as React context and as CSS
 * custom properties on the document root.
 */
export const AppViewportProvider: React.FC<AppViewportProviderProps> = ({
	chrome,
	children,
}) => {
	const chromeRef = useRef<HTMLDivElement>(null);
	const [chromeHeight, setChromeHeight] = useState(0);

	useLayoutEffect(() => {
		const element = chromeRef.current;
		if (!element) {
			return;
		}
		// A banner mounting changes the chrome's height without a resize
		// event, which is exactly the case the old per-view listeners missed.
		const observer = new ResizeObserver(() => {
			setChromeHeight(element.getBoundingClientRect().height);
		});
		observer.observe(element);
		setChromeHeight(element.getBoundingClientRect().height);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		const root = document.documentElement;
		root.style.setProperty(CHROME_HEIGHT_VAR, `${chromeHeight}px`);
		root.style.setProperty(
			AVAILABLE_HEIGHT_VAR,
			`calc(100vh - ${chromeHeight}px - ${SUPPORT_BUBBLE_GUTTER_PX}px)`
		);
	}, [chromeHeight]);

	return (
		<AppViewportContext.Provider value={{ chromeHeight }}>
			<div ref={chromeRef} data-component="app-chrome">
				{chrome}
			</div>
			{/* The bubble's corner, reserved. Padding rather than a margin so
			    `bottomInsetOf` counts it for every measured pane too, and the
			    same number comes out of AVAILABLE_HEIGHT above — a view that
			    fills the window and one that scrolls both stop short of it. */}
			<div
				data-component="app-content"
				style={{ paddingBottom: SUPPORT_BUBBLE_GUTTER_PX }}
			>
				{children}
			</div>
		</AppViewportContext.Provider>
	);
};

interface AvailableHeight<T extends HTMLElement> {
	/** Attach to the element that should fill the rest of the viewport. */
	ref: React.RefObject<T | null>;
	/** Its height, as a CSS length. */
	height: string;
}

/**
 * Space the element's ancestors keep below it — their bottom padding and
 * borders, its own bottom margin, and any sibling sitting underneath.
 *
 * Filling to the bottom of the viewport is not the same as filling to the
 * bottom of the space you are allowed: a page container with `py: 2` owes 16px
 * below you, and claiming it is what puts a scrollbar on a "full screen" view.
 */
const bottomInsetOf = (element: HTMLElement): number => {
	let inset = 0;
	let node: HTMLElement = element;
	while (node.parentElement) {
		const parent = node.parentElement;
		inset += Math.max(
			parent.getBoundingClientRect().bottom - node.getBoundingClientRect().bottom,
			0
		);
		node = parent;
	}
	return inset;
};

/**
 * The height an element has from where it starts to the bottom of the
 * viewport, kept correct as the chrome grows and shrinks.
 *
 * Measures the element's own top rather than subtracting a guess, so a page
 * header, tab bar or breadcrumb above it needs no constant of its own.
 */
export function useAvailableHeight<T extends HTMLElement>(): AvailableHeight<T> {
	const { chromeHeight } = useContext(AppViewportContext);
	const ref = useRef<T>(null);
	const [height, setHeight] = useState<string>(AVAILABLE_HEIGHT);

	useLayoutEffect(() => {
		const measure = () => {
			const element = ref.current;
			if (!element) {
				return;
			}
			// The element's top in the document, not in the viewport. A client
			// route change keeps the previous page's scroll offset, so a view
			// that mounts before the browser has clamped it measured its top
			// that many pixels too high and sized itself that much too tall —
			// leaving the page scrollable by exactly the offset it inherited.
			const top = element.getBoundingClientRect().top + window.scrollY;
			const available = window.innerHeight - top - bottomInsetOf(element);
			setHeight(`${Math.max(available, MIN_PANE_PX)}px`);
		};
		measure();
		window.addEventListener("resize", measure);
		return () => window.removeEventListener("resize", measure);
		// chromeHeight is the dependency that matters: a banner appearing
		// moves this element down without firing a resize.
	}, [chromeHeight]);

	return { ref, height };
}
